/**
 * Planning Assumptions — the dashboard mirror of planscout-api/config/planningAssumptions.js.
 * Keep the two in lockstep (block names, keys, max, baselines, paths).
 *
 * Case assumptions are the plan-level numbers stamped on a SIPS case at creation (COLA /
 * inflation, account returns by tax bucket, taxable-account tax treatment). They are set
 * per firm → branch → advisor (merged per key, resolved live) and overridable per plan
 * from the run panel's Planning Assumptions card.
 */
import type {
  CaseAssumptions,
  ResolvedCaseAssumptions,
  SipsColaRates,
  SipsNqTaxRates,
  SipsTaxBucketReturnRates,
} from './advisor';

export type { ResolvedCaseAssumptions } from './advisor';

export type CaseAssumptionBlock = 'assetReturnRates' | 'taxBucketReturnRates' | 'colaRates' | 'nqTaxRates';
/** The three case-creation paths; each resolves exactly the blocks its baseline defines. */
export type CaseAssumptionPath = 'intake' | 'api' | 'webform';
/** The blocks a dashboard run may override per plan (and the only ones the editors show). */
export type PerCaseBlock = 'taxBucketReturnRates' | 'colaRates' | 'nqTaxRates';

export const RETURN_RATE_KEYS = ['checkingSavings', 'annuity', 'invested', 'lifeInsurance'] as const;
export const TAX_BUCKET_RETURN_KEYS = ['taxDeferred', 'taxable', 'taxFree'] as const;
export const COLA_RATE_KEYS = ['wages', 'socialSecurity', 'pension', 'targetIncome'] as const;
export const NQ_TAX_RATE_KEYS = ['dividendPercent', 'turnoverPercent'] as const;
/** Accepted and stored, hidden in every editor until SIPS supports them. */
export const NQ_TAX_RESERVED_KEYS = ['qualifiedDividendPercent', 'taxableInterestPercent'] as const;
export const NQ_RESERVED_KEYS_ENABLED = false;

export const MAX_RETURN_PERCENT = 20;
export const MAX_COLA_PERCENT = 10;
export const MAX_NQ_PERCENT = 100;

/** PlanScout default returns by tax bucket on every non-intake path. */
export const TAX_BUCKET_RETURN_BASELINE: Required<SipsTaxBucketReturnRates> = { taxDeferred: 5, taxable: 5, taxFree: 5 };
/** PlanScout default COLA / inflation on the partner API and the dashboard / webform: 2.6% wages / Social Security / target income, 0% pension (2026-10-23). */
export const COLA_BASELINE: Required<SipsColaRates> = { wages: 2.6, socialSecurity: 2.6, pension: 0, targetIncome: 2.6 };
/** PlanScout default NQ tax treatment on every path (= the SIPS SC01 constants). */
export const NQ_TAX_BASELINE = { dividendPercent: 2, turnoverPercent: 30 } as const;

export interface CaseAssumptionBlockDef {
  name: CaseAssumptionBlock;
  keys: readonly string[];
  max: number;
  paths: readonly CaseAssumptionPath[];
  /** Shown in the firm / advisor editors and the per-plan card. */
  ui: boolean;
}

/**
 * The paths firm / branch / advisor / per-plan case assumptions apply to. The Zoho
 * intake is its own path (2026-10-22): a fixed platform set (SOP account-type returns,
 * COLA 0 / 1.5 / 0 / 2, NQ 2 / 30) that takes NO overrides, so no block lists it.
 */
const OVERRIDABLE_PATHS: readonly CaseAssumptionPath[] = ['api', 'webform'];

export const CASE_ASSUMPTION_BLOCKS: readonly CaseAssumptionBlockDef[] = [
  // The intake's account-type (SOP) model: fixed, no editor, resolves on no path.
  { name: 'assetReturnRates', keys: RETURN_RATE_KEYS, max: MAX_RETURN_PERCENT, paths: [], ui: false },
  { name: 'taxBucketReturnRates', keys: TAX_BUCKET_RETURN_KEYS, max: MAX_RETURN_PERCENT, paths: OVERRIDABLE_PATHS, ui: true },
  { name: 'colaRates', keys: COLA_RATE_KEYS, max: MAX_COLA_PERCENT, paths: OVERRIDABLE_PATHS, ui: true },
  { name: 'nqTaxRates', keys: [...NQ_TAX_RATE_KEYS, ...NQ_TAX_RESERVED_KEYS], max: MAX_NQ_PERCENT, paths: OVERRIDABLE_PATHS, ui: true },
];

export const CASE_ASSUMPTION_BLOCK_NAMES: readonly CaseAssumptionBlock[] = CASE_ASSUMPTION_BLOCKS.map((b) => b.name);
export const blocksForPath = (path: CaseAssumptionPath) => CASE_ASSUMPTION_BLOCKS.filter((b) => b.paths.includes(path));
export const UI_BLOCKS: readonly CaseAssumptionBlockDef[] = CASE_ASSUMPTION_BLOCKS.filter((b) => b.ui);
export const PER_CASE_BLOCKS: readonly CaseAssumptionBlockDef[] = blocksForPath('webform');
export const PER_CASE_BLOCK_NAMES: readonly PerCaseBlock[] = PER_CASE_BLOCKS.map((b) => b.name as PerCaseBlock);

/** Keys of a block the UI shows (reserved NQ keys hidden while the flag is off). */
export const visibleKeys = (block: CaseAssumptionBlockDef): readonly string[] =>
  NQ_RESERVED_KEYS_ENABLED ? block.keys : block.keys.filter((k) => !(NQ_TAX_RESERVED_KEYS as readonly string[]).includes(k));

/** Display labels, shared by the firm / advisor editors and the per-plan card. */
export const RATE_LABELS: Record<PerCaseBlock, Record<string, string>> = {
  taxBucketReturnRates: {
    taxDeferred: 'Tax-Deferred (IRA / 401k)',
    taxable: 'Taxable (NQ)',
    taxFree: 'Tax-Free (Roth)',
  },
  colaRates: {
    wages: 'Wages',
    socialSecurity: 'Social Security',
    pension: 'Pension',
    targetIncome: 'Inflation rate (target income)',
  },
  nqTaxRates: {
    dividendPercent: 'Dividend yield',
    turnoverPercent: 'Annual turnover',
    qualifiedDividendPercent: 'Qualified dividends',
    taxableInterestPercent: 'Taxable interest rate',
  },
};

/** Human label for a case-creation path (editor fallback text). */
export const PATH_LABELS: Record<CaseAssumptionPath, string> = { intake: 'intake', api: 'API', webform: 'dashboard' };

/** Sparse per-plan overrides (keys set only), the create-case `caseAssumptionOverrides` body. */
export type CaseAssumptionOverrides = Pick<CaseAssumptions, PerCaseBlock>;

const isRate = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Overlay sparse layers (firm → branch → advisor effective, then a plan's overrides) on a
 * full baseline, per key, for the per-plan blocks. A key is taken only when it is a finite
 * number — 0 is a real value. Returns a FULL snapshot (every key of every per-plan block).
 */
export function resolveCaseAssumptions(
  baseline: ResolvedCaseAssumptions,
  ...layers: (CaseAssumptions | CaseAssumptionOverrides | null | undefined)[]
): ResolvedCaseAssumptions {
  const out: Record<string, any> = { ...baseline };
  for (const block of PER_CASE_BLOCKS) {
    const merged: Record<string, number> = { ...((baseline as any)[block.name] || {}) };
    for (const layer of layers) {
      const src = (layer as any)?.[block.name];
      if (!src || typeof src !== 'object') continue;
      for (const key of block.keys) {
        if (isRate(src[key])) merged[key] = src[key];
      }
    }
    out[block.name] = merged;
  }
  return out as ResolvedCaseAssumptions;
}

/**
 * Sparse diff of a fully-resolved draft against the advisor's effective snapshot — what the
 * run panel sends as `caseAssumptionOverrides`. A key is included when the draft value is a
 * finite number that differs from the base (0 vs 5 is a diff). Empty blocks are dropped;
 * null when nothing differs. Mirrors utils/planPreferenceOverrides.computePreferenceOverrides.
 */
export function computeCaseAssumptionOverrides(
  base: ResolvedCaseAssumptions,
  draft: ResolvedCaseAssumptions
): CaseAssumptionOverrides | null {
  const out: Record<string, Record<string, number>> = {};
  for (const block of PER_CASE_BLOCKS) {
    const b = ((base as any)[block.name] || {}) as Record<string, number | undefined>;
    const d = ((draft as any)[block.name] || {}) as Record<string, number | undefined>;
    for (const key of block.keys) {
      const dv = d[key];
      if (isRate(dv) && dv !== b[key]) {
        (out[block.name] ||= {})[key] = dv;
      }
    }
  }
  return Object.keys(out).length > 0 ? (out as CaseAssumptionOverrides) : null;
}

/** Number of overridden keys across blocks. */
export const countCaseAssumptionOverrides = (o: CaseAssumptionOverrides | null | undefined): number =>
  !o ? 0 : Object.values(o).reduce((n, block) => n + Object.keys(block || {}).length, 0);

/** True when the layer sets at least one key in any block. */
export const hasAnyCaseAssumptions = (a: CaseAssumptions | null | undefined): boolean =>
  CASE_ASSUMPTION_BLOCK_NAMES.some((name) => Object.keys((a as any)?.[name] || {}).length > 0);

/** Narrow helpers for typed access in the editors / card. */
export type ColaKey = keyof SipsColaRates;
export type TaxBucketKey = keyof SipsTaxBucketReturnRates;
export type NqTaxKey = keyof SipsNqTaxRates;
